import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Alert, Button, Container, TextInput } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image } from '../../components';
import { useSelector } from 'react-redux';
import { useUtilityFunction } from '../../hooks/use-utility-functions';
import { useBranchData } from '../../hooks/use-branch-data';
import { isValidArray, isValidPostal } from '../../components/utilities/validations';

import BranchPromoPanel from '../../components/common/branch-promo-panel';
import CardSection from '../../components/common/card-section';
import FAQ from '../../components/common/faq';
import RTASimpleForm from '../../components/common/forms/rta-simple-form';
import FinancingBlock from '../../components/common/financing-block';
import GreenskyFinancing from '../../components/template/financing/greensky';

const Financing = (props) => {
	const pageData = props.data.contentstackFinancing;
	const { updateLocation } = useUtilityFunction();
	const branchData = useBranchData();
	const locations = useSelector((state) => state.locations);
	const [postalCode, setPostalCode] = useState({ zip: '', oldZip: '', status: 'idle' });
	const [branchID, setBranchID] = useState(null);
	const [financingData, setFinancingData] = useState({});

	useEffect(() => {
		if (postalCode.status !== 'entered') return;

		if (locations?.zone?.retail_branch_id) {
			setBranchID(locations.zone.retail_branch_id);
		}

		if (postalCode.zip === postalCode.oldZip) {
			setFinancingData(branchData?.find((branch) => branch.branch_retail?.includes(branchID)));
			setPostalCode((current) => ({ ...current, status: 'fetched' }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- branchID cause incorrect data due to race condition
	}, [postalCode, locations?.zone]);

	useEffect(() => {
		if (!branchID) return;

		setFinancingData(branchData?.find((branch) => branch.branch_retail?.includes(branchID)));
		setPostalCode((current) => ({ ...current, oldZip: current.zip, status: 'fetched' }));
	}, [branchData, branchID]);

	const onPostalChange = (e) => {
		setPostalCode({ ...postalCode, zip: e.target.value, status: 'idle', error: null });
		setFinancingData(null);
	};

	const onPostalFormSubmit = (e) => {
		e.preventDefault();

		setPostalCode({ ...postalCode, status: 'processing' });

		if (!isValidPostal(postalCode.zip)) {
			setPostalCode({ ...postalCode, error: 'Please enter a valid zip code', status: 'idle' });
			return;
		}

		updateLocation(postalCode.zip);
		setTimeout(() => setPostalCode({ ...postalCode, status: 'entered' }), 500);
	};

	return (
		<Layout id='template-Financing'>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.hero && (
				<div className='financingHero bg-black py-12 md:py-16 xl:py-20'>
					<Container>
						{pageData.hero.heading && (
							<h1
								className='text-white'
								dangerouslySetInnerHTML={{ __html: pageData.hero.heading }}
							/>
						)}
						{pageData.hero.subheading && (
							<div
								className='text-white'
								dangerouslySetInnerHTML={{ __html: pageData.hero.subheading }}
							/>
						)}
					</Container>
				</div>
			)}

			{pageData.modular_blocks?.[0] &&
				pageData.modular_blocks.map((block, index) => {
					if (block.local_offer != null && branchID != null) {
						return (
							<BranchPromoPanel
								key={index}
								branchID={branchID}
							/>
						);
					}

					if (block.financing_options != null) {
						const greenskyContent = block.financing_options.finance[0];
						const rtaContent = block.financing_options.finance[1];

						return (
							<div
								key={index}
								className='financingOptions py-6 md:py-8 xl:py-12'
							>
								<Container>
									<div className='grid md:grid-cols-2 md:items-center md:gap-x-8 xl:items-start xl:gap-x-12'>
										{block.financing_options.image?.[0] && (
											<Image
												data={block.financing_options.image[0]}
												loading='eager'
												className='col-start-1 mb-6 aspect-[4/3] md:mb-8 xl:row-span-4 xl:mb-0'
											/>
										)}

										{block.financing_options.heading && (
											<h2
												className='row-start-2 mb-4 font-normal !leading-tight md:col-start-2 md:row-start-1 md:mb-0 xl:mb-8'
												dangerouslySetInnerHTML={{ __html: block.financing_options.heading }}
											/>
										)}

										{block.financing_options.description && (
											<div
												dangerouslySetInnerHTML={{ __html: block.financing_options.description }}
												className='col-start-1 mb-6 md:col-end-3 xl:col-start-2 xl:mb-8'
											/>
										)}

										<div className='financingOptions-form col-start-1 md:col-end-3 xl:col-start-2'>
											{block.financing_options.zip_code_text && (
												<div
													dangerouslySetInnerHTML={{ __html: block.financing_options.zip_code_text }}
													className='mb-3 text-lg font-semibold md:text-xl'
												/>
											)}

											{postalCode.error && (
												<Alert
													type='error'
													className='mb-4'
													hideClose
												>
													{postalCode.error}
												</Alert>
											)}

											<form
												className='flex flex-row'
												onSubmit={onPostalFormSubmit}
											>
												<TextInput
													id='postalCode'
													label='Zip Code'
													onChange={onPostalChange}
													value={postalCode.zip}
													placeholder='Enter Zip Code'
													autoComplete='postal-code'
													groupClass='!mb-0'
													error={postalCode.error ? true : false}
												/>

												<Button
													type='submit'
													color='primary-alt'
													size='sm'
													className='!w-auto px-5'
													disabled={postalCode.status !== 'idle'}
												>
													{['entered', 'processing'].includes(postalCode.status) ? 'Processing..' : 'Submit'}
												</Button>
											</form>

											{postalCode.status === 'fetched' && isValidArray(financingData?.financing_options) && (
												<div
													key={index}
													className='mt-5 border border-gray-10 bg-gray-5 p-5'
												>
													{greenskyContent?.heading && <p className='mb-2 text-lg font-semibold md:text-xl'>{greenskyContent.heading}</p>}
													{greenskyContent?.description && (
														<div
															className='mb-2 text-sm [&_a]:decoration-black'
															dangerouslySetInnerHTML={{ __html: greenskyContent.description }}
														/>
													)}

													{financingData.financing_options.map((option, index) => (
														<div key={index}>
															{option.embed_option?.description && <GreenskyFinancing data={option.embed_option} />}
															{isValidArray(option.manual_option) &&
																option.manual_option.map((manual, index) => (
																	<FinancingBlock
																		key={index}
																		data={manual}
																		view={manual.manual_type}
																	/>
																))}
														</div>
													))}
												</div>
											)}

											{postalCode.status === 'fetched' && !isValidArray(financingData?.financing_options) && !financingData?.financing_error && (
												<div
													key={index}
													className='mt-5 border border-gray-10 bg-gray-5 p-5'
												>
													{rtaContent?.heading && <p className='mb-2 text-lg font-semibold md:text-xl'>{rtaContent.heading}</p>}
													{rtaContent?.description && (
														<div
															className='mb-2 text-sm [&_a]:decoration-black'
															dangerouslySetInnerHTML={{ __html: rtaContent.description }}
														/>
													)}

													<RTASimpleForm />
												</div>
											)}

											{postalCode.status === 'fetched' && !isValidArray(financingData?.financing_options) && financingData?.financing_error && (
												<Alert
													type='error'
													className='mt-5'
													hideClose
												>
													<div dangerouslySetInnerHTML={{ __html: financingData.financing_error }} />
												</Alert>
											)}
										</div>
									</div>
								</Container>
							</div>
						);
					}

					if (block.faqs != null) {
						return (
							<div
								key={index}
								className='financingFaq py-10 md:py-12 xl:py-16'
							>
								<Container>
									{block.faqs.heading && (
										<h2
											className='mb-4 text-center'
											dangerouslySetInnerHTML={{ __html: block.faqs.heading }}
										/>
									)}

									{isValidArray(block.faqs.faqs) && (
										<FAQ
											data={block.faqs.faqs}
											multipleColumns
										/>
									)}
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								key={index}
								data={block.cards}
							/>
						);
					}
				})}
		</Layout>
	);
};

Financing.propTypes = {
	data: PropTypes.object,
};

export default Financing;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackFinancing.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackFinancing(id: { eq: $uid }) {
			url
			breadcrumbs {
				...Breadcrumbs
			}
			hero {
				background_hex_value
				heading
				subheading
			}
			modular_blocks {
				local_offer {
					background_hex_value
					anchor_text
					heading
				}
				financing_options {
					heading
					description
					zip_code_text
					branch_id
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
					finance {
						finance_type
						heading
						description
						financing_html
					}
				}
				faqs {
					heading
					faqs {
						question
						answer
					}
				}
				cards {
					...CardSection
				}
			}
			seo {
				...SEO
			}
		}
	}
`;
